/* pdp2-jar — the scroll-scrubbed jar chapter (#why-blue)
 *
 * One sticky stage, one video, scrubbed by scroll. Four tiers, so the chapter
 * is never broken:
 *   1. frames  — the video is played once, muted and unseen, at double speed;
 *                every presented frame is captured to a compressed blob via
 *                requestVideoFrameCallback. Scrolling decodes a sliding window
 *                of those blobs and draws them on a canvas. Linear decode is
 *                immune to keyframe density, seeking is not.
 *   2. seek    — when capture is unsupported or fails, currentTime is set from
 *                the scroll (seeks coalesced) and the video is drawn to the
 *                same canvas on 'seeked'.
 *   3. still   — no video yet: the poster is drawn as the stage and the copy
 *                still plays with the scroll (the launch state before a film).
 *   4. poster  — reduced motion, decode error or a file over the
 *                duration limit: the poster and the full copy stay, static.
 *
 * Nothing autoplays visibly: the video element is two pixels wide and never
 * shown, so the cream can never appear to animate on its own.
 *
 * Poses. Desktop: cover, anchored to an edge, vertically centred; the beats
 * switch at their "at" percent of the chapter scroll. Phones: the copy owns the
 * top of the stage and the whole subject sits below it in one static pose,
 * measured from the real copy height; the beats rise in one after another,
 * then the film runs from film_start to film_end.
 */
(function () {
  'use strict';

  if (window.__pdp2JarLoaded) return;
  window.__pdp2JarLoaded = true;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var DESKTOP = window.matchMedia('(min-width: 900px)');
  var teardowns = {};

  function num(v, d) {
    var n = parseFloat(v);
    return isNaN(n) ? d : n;
  }
  function clamp(v, a, b) {
    return Math.min(b, Math.max(a, v));
  }
  // Position of p inside [a, b], clamped to 0..1.
  function seg(p, a, b) {
    if (b <= a) return p >= b ? 1 : 0;
    return clamp((p - a) / (b - a), 0, 1);
  }
  function hexToRgb(hex) {
    var h = String(hex || '').trim().replace('#', '');
    if (h.length === 3) h = h.replace(/(.)/g, '$1$1');
    var n = parseInt(h, 16);
    if (isNaN(n) || h.length !== 6) return [20, 25, 61];
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }

  function initJar(root) {
    if (root.__pdp2Jar) return;
    root.__pdp2Jar = true;

    var d = root.dataset;
    var cfg = {
      holdStart: num(d.holdStart, 6) / 100,
      holdEnd: num(d.holdEnd, 14) / 100,
      filmStart: num(d.filmStart, 32) / 100,
      filmEnd: num(d.filmEnd, 94) / 100,
      beatsStart: num(d.beatsStart, 8) / 100,
      anchor: d.desktopAnchor || 'right',
      zoom: num(d.desktopZoom, 100) / 100,
      focus: num(d.mobileFocus, 66) / 100,
      subjectTop: num(d.subjectTop, 32) / 100,
      subjectBottom: num(d.subjectBottom, 82) / 100,
      headroom: num(d.subjectHeadroom, 12) / 100,
      maxFrames: Math.max(12, num(d.maxFrames, 120)),
      maxDuration: num(d.maxDuration, 12),
      maxWidth: num(d.sourceWidth, 1280),
      bottomGap: num(d.mobileBottomGap, 16),
      quality: 0.82
    };

    var stage = root.querySelector('[data-pdp2-jar-stage]');
    var canvas = root.querySelector('[data-pdp2-jar-canvas]');
    var video = root.querySelector('[data-pdp2-jar-video]');
    var poster = root.querySelector('[data-pdp2-jar-poster]');
    var copy = root.querySelector('[data-pdp2-jar-copy]');
    var link = root.querySelector('[data-pdp2-jar-link]');
    var hint = root.querySelector('[data-pdp2-jar-hint]');
    var notice = root.querySelector('[data-pdp2-jar-notice]');
    var beats = Array.prototype.map.call(root.querySelectorAll('[data-pdp2-jar-beat]'), function (el) {
      return { el: el, bar: el.querySelector('[data-pdp2-jar-bar]'), at: num(el.dataset.at, 0) / 100 };
    });
    if (!stage || !canvas) return;

    function setState(s, why) {
      root.dataset.pdp2JarState = s;
      if (notice) {
        if (s === 'poster' && why) {
          notice.hidden = false;
          notice.textContent = 'Jar chapter: showing the poster only (' + why + ').';
        } else {
          notice.hidden = true;
        }
      }
    }

    // One static presentation for reduced motion, missing/failed video and
    // stages too short for the copy: poster in flow, all copy visible.
    var halted = false;
    function goStatic(why) {
      halted = true;
      root.classList.remove('is-motion', 'is-live');
      root.classList.add('is-static');
      root.style.removeProperty('--pdp2-jar-veil');
      setState('poster', why);
    }

    if (reduceMotion.matches) {
      goStatic();
      return;
    }
    root.classList.add('is-motion');

    var ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) {
      goStatic('no canvas');
      return;
    }

    var rgb = hexToRgb(getComputedStyle(root).getPropertyValue('--pdp2-jar-ink'));
    var ink = 'rgb(' + rgb.join(',') + ')';
    var inkClear = 'rgba(' + rgb.join(',') + ',0)';

    var mode = 'poster'; // poster | still | frames | seek
    var frames = []; // [{ t, blob }]
    var bitmaps = {}; // index -> ImageBitmap
    var decoding = {};
    var natW = 0;
    var natH = 0;
    var cw = 0;
    var ch = 0;
    var dpr = 1;
    var pose = null;
    var lastDrawn = -1;
    var target = -1;
    var raf = 0;
    var active = false;
    var prepared = false;
    var destroyed = false;

    function isDesktop() {
      return DESKTOP.matches;
    }

    /* ---- poses ---------------------------------------------------------- */

    function computePose() {
      if (!natW || !natH || !cw || !ch) return null;
      var cover = Math.max(cw / natW, ch / natH);
      var copyH = copy ? copy.offsetHeight : 0;

      if (isDesktop()) {
        var s = cover * cfg.zoom;
        var w = natW * s;
        var h = natH * s;
        var x = cfg.anchor === 'left' ? 0 : cfg.anchor === 'center' ? (cw - w) / 2 : cw - w;
        root.style.setProperty('--pdp2-jar-veil', '0%');
        if (copyH > ch - 40) {
          goStatic('copy taller than the stage');
          return null;
        }
        return { s: s, x: x, y: (ch - h) / 2 };
      }

      // Phone: the copy owns the top; the whole subject sits below it with its
      // headroom (lid, cream peak) clearing the last line of copy by 20px.
      var copyBottom = copy ? copy.offsetTop + copy.offsetHeight : ch * 0.5;
      var room = ch - cfg.bottomGap - (copyBottom + 20);
      // Less than about a quarter of the stage left for the subject: the copy
      // cannot share a pinned stage with the film on this screen.
      if (room < ch * 0.26) {
        goStatic('copy leaves no room for the subject');
        return null;
      }
      var span = (cfg.subjectBottom - cfg.subjectTop + cfg.headroom) * natH;
      var sm = Math.min(cover * 0.9, room / span);
      // Centre the subject, but never let a frame edge show inside the stage.
      var wm = natW * sm;
      var xm = cw / 2 - cfg.focus * wm;
      if (wm >= cw) xm = Math.min(0, Math.max(cw - wm, xm));
      else xm = (cw - wm) / 2;
      var ym = ch - cfg.bottomGap - cfg.subjectBottom * natH * sm;
      // The veil fades out around the copy's bottom edge, mostly above it
      // (CSS adds the stops), so the jar and its lid are clear of ink.
      root.style.setProperty('--pdp2-jar-veil', ((copyBottom / ch) * 100).toFixed(2) + '%');
      return { s: sm, x: xm, y: ym };
    }

    function measure() {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      cw = stage.clientWidth;
      ch = stage.clientHeight;
      if (!natW && poster && poster.naturalWidth) {
        natW = poster.naturalWidth;
        natH = poster.naturalHeight;
      }
      var pw = Math.round(cw * dpr);
      var ph = Math.round(ch * dpr);
      if (canvas.width !== pw || canvas.height !== ph) {
        canvas.width = pw;
        canvas.height = ph;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      pose = computePose();
      lastDrawn = -1;
    }

    function drawSource(src) {
      if (!pose || !cw || !ch) return;
      var w = natW * pose.s;
      var h = natH * pose.s;
      ctx.fillStyle = ink;
      ctx.fillRect(0, 0, cw, ch);
      try {
        ctx.drawImage(src, pose.x, pose.y, w, h);
      } catch (e) {
        return;
      }
      if (!isDesktop()) {
        // The frame's own top edge dissolves into the ink, so no seam can show.
        var fade = h * 0.16;
        var g = ctx.createLinearGradient(0, pose.y, 0, pose.y + fade);
        g.addColorStop(0, ink);
        g.addColorStop(1, inkClear);
        ctx.fillStyle = g;
        ctx.fillRect(0, pose.y - 1, cw, fade + 1);
      }
      root.classList.add('is-live');
    }

    /* ---- scroll progress and copy choreography -------------------------- */

    function progress() {
      var r = root.getBoundingClientRect();
      var sc = r.height - stage.offsetHeight;
      if (sc <= 0) return 0;
      var top = parseFloat(getComputedStyle(stage).top) || 0;
      return clamp((top - r.top) / sc, 0, 1);
    }

    // Fraction of the film for a chapter progress.
    function filmFraction(p) {
      return isDesktop() ? seg(p, cfg.holdStart, 1 - cfg.holdEnd) : seg(p, cfg.filmStart, cfg.filmEnd);
    }

    function updateCopy(p) {
      var n = beats.length;
      if (isDesktop()) {
        var current = 0;
        beats.forEach(function (b, i) {
          if (p >= b.at) current = i;
        });
        beats.forEach(function (b, i) {
          b.el.classList.add('is-in');
          b.el.classList.toggle('is-on', i === current);
          var next = i < n - 1 ? beats[i + 1].at : 1;
          if (b.bar) b.bar.style.setProperty('--pdp2-fill', i < current ? 1 : i > current ? 0 : seg(p, b.at, next));
        });
        if (link) link.classList.add('is-in');
      } else {
        var span = 0.06;
        var start = cfg.beatsStart;
        var end = Math.max(start, cfg.filmStart - span);
        var gap = n > 1 ? (end - start) / (n - 1) : 0;
        beats.forEach(function (b, i) {
          var t0 = start + gap * i;
          b.el.classList.add('is-on');
          b.el.classList.toggle('is-in', p >= t0 + 0.005);
          var next = i < n - 1 ? start + gap * (i + 1) : cfg.filmStart;
          if (b.bar) b.bar.style.setProperty('--pdp2-fill', seg(p, t0, next));
        });
        if (link) link.classList.toggle('is-in', p >= cfg.filmEnd - 0.02);
      }
      if (hint) hint.classList.toggle('is-hidden', p > 0.04);
    }

    /* ---- tier 1: frame cache -------------------------------------------- */

    function decode(i) {
      if (destroyed || halted || bitmaps[i] || decoding[i] || !frames[i] || !frames[i].blob) return;
      decoding[i] = true;
      createImageBitmap(frames[i].blob).then(
        function (bm) {
          decoding[i] = false;
          if (destroyed || halted) {
            bm.close();
            return;
          }
          bitmaps[i] = bm;
          // Whatever asked for it, if this is the frame the scroll wants now,
          // show it (a prefetch that became the target included).
          if (target === i && mode === 'frames') {
            drawSource(bm);
            lastDrawn = i;
          }
        },
        function () {
          decoding[i] = false;
          fallbackToSeek('frame decode failed');
        }
      );
    }

    function prefetch(i) {
      for (var k = 1; k <= 6; k++) {
        decode(i + k);
        decode(i - k);
      }
      Object.keys(bitmaps).forEach(function (key) {
        var j = +key;
        if (Math.abs(j - i) > 24) {
          bitmaps[j].close();
          delete bitmaps[j];
        }
      });
    }

    function wantFrame(i) {
      target = i;
      if (bitmaps[i]) {
        if (lastDrawn !== i) {
          drawSource(bitmaps[i]);
          lastDrawn = i;
        }
      } else {
        decode(i);
      }
      prefetch(i);
    }

    function harvest(done, fail) {
      if (
        !('requestVideoFrameCallback' in HTMLVideoElement.prototype) ||
        typeof createImageBitmap !== 'function' ||
        !HTMLCanvasElement.prototype.toBlob
      ) {
        return fail('capture unsupported');
      }
      var hw = Math.min(natW, cfg.maxWidth);
      var hh = Math.round((natH * hw) / natW);
      var hc = document.createElement('canvas');
      hc.width = hw;
      hc.height = hh;
      var hctx = hc.getContext('2d', { alpha: false });
      var type = hc.toDataURL('image/webp').indexOf('data:image/webp') === 0 ? 'image/webp' : 'image/jpeg';
      var pending = 0;
      var finished = false;
      var failed = false;
      var timer = 0;
      var minGap = video.duration / cfg.maxFrames;
      var lastT = -Infinity;

      function bail(why) {
        if (finished) return;
        finished = true;
        failed = true;
        clearTimeout(timer);
        video.pause();
        frames = [];
        fail(why);
      }

      function finish() {
        if (finished) return;
        finished = true;
        clearTimeout(timer);
        video.pause();
        (function settle() {
          if (destroyed) return;
          if (pending > 0) return setTimeout(settle, 30);
          frames = frames
            .filter(function (f) {
              return f.blob;
            })
            .sort(function (a, b) {
              return a.t - b.t;
            });
          var last = frames.length ? frames[frames.length - 1].t : 0;
          if (frames.length < 12 || last < video.duration * 0.8) {
            frames = [];
            return fail('capture incomplete');
          }
          done();
        })();
      }

      function onFrame(now, meta) {
        if (finished || destroyed || halted) return;
        var t = meta.mediaTime;
        if (t - lastT >= minGap * 0.98) {
          lastT = t;
          try {
            hctx.drawImage(video, 0, 0, hw, hh);
          } catch (e) {
            return bail('cross-origin video');
          }
          var rec = { t: t, blob: null };
          frames.push(rec);
          pending++;
          try {
            hc.toBlob(
              function (b) {
                rec.blob = b;
                pending--;
              },
              type,
              cfg.quality
            );
          } catch (e) {
            pending--;
            return bail('capture blocked');
          }
        }
        if (frames.length >= cfg.maxFrames) return finish();
        video.requestVideoFrameCallback(onFrame);
      }

      video.addEventListener('ended', finish, { once: true });
      timer = setTimeout(finish, (video.duration / 2) * 1000 + 6000);
      video.requestVideoFrameCallback(onFrame);
      video.currentTime = 0;
      try {
        video.playbackRate = 2;
      } catch (e) {
        /* some engines reject rates while not playing; 1x still works */
      }
      var pr = video.play();
      if (pr && pr.catch) {
        pr.catch(function () {
          if (!failed) bail('autoplay blocked');
        });
      }
    }

    /* ---- tier 2: seek scrub --------------------------------------------- */

    var seeking = false;
    var pendingT = -1;
    var seekWired = false;

    function fallbackToSeek(why) {
      if (destroyed || halted || mode === 'seek') return;
      Object.keys(bitmaps).forEach(function (k) {
        bitmaps[k].close();
      });
      bitmaps = {};
      frames = [];
      mode = 'seek';
      setState('seek', why);
      lastDrawn = -1;
      try {
        video.playbackRate = 1;
      } catch (e) {
        /* ignore */
      }
      video.pause();
      if (!seekWired) {
        seekWired = true;
        video.addEventListener('seeked', onSeeked);
      }
      if (!video.getAttribute('src')) {
        video.src = video.dataset.src;
        video.load();
        video.addEventListener(
          'loadedmetadata',
          function () {
            if (!destroyed && !halted) schedule();
          },
          { once: true }
        );
      } else {
        schedule();
      }
    }

    function wantTime(t) {
      if (!isFinite(t)) return;
      if (seeking) {
        pendingT = t;
        return;
      }
      if (lastDrawn >= 0 && Math.abs((video.currentTime || 0) - t) < 0.02) return;
      seeking = true;
      video.currentTime = Math.max(0.001, Math.min(t, video.duration - 0.001));
    }

    function onSeeked() {
      if (mode !== 'seek' || destroyed) return;
      drawSource(video);
      lastDrawn = 1;
      seeking = false;
      if (pendingT >= 0) {
        var t = pendingT;
        pendingT = -1;
        wantTime(t);
      }
    }

    /* ---- render loop ---------------------------------------------------- */

    function render() {
      raf = 0;
      if (destroyed || halted) return;
      var p = progress();
      updateCopy(p);
      var f = filmFraction(p);
      if (mode === 'frames' && frames.length) {
        wantFrame(Math.round(f * (frames.length - 1)));
      } else if (mode === 'seek' && video && video.duration) {
        wantTime(f * video.duration);
      }
    }

    function schedule() {
      if (!raf) raf = requestAnimationFrame(render);
    }

    function relayout() {
      if (halted) return;
      measure();
      if (halted) return;
      if (mode === 'frames' && bitmaps[target]) {
        drawSource(bitmaps[target]);
        lastDrawn = target;
      } else if (mode === 'seek' && video && video.readyState >= 2) {
        drawSource(video);
      } else if (poster && poster.complete && poster.naturalWidth && natW) {
        drawSource(poster);
      }
      schedule();
    }

    /* ---- preparation ---------------------------------------------------- */

    function stayPoster(why) {
      mode = 'poster';
      goStatic(why);
    }

    // No video yet: the poster is the whole film. The stage still pins and the
    // beats still play with the scroll; only the picture holds one frame. When
    // a video is added later, the same chapter scrubs it with no other change.
    function showStill() {
      if (!poster) return stayPoster('no video and no poster');
      mode = 'still';
      setState('still');
      // No frames to scrub, so the chapter is only as long as its copy. Desktop:
      // the first point shows as the stage pins, each mouse-wheel step (about
      // 100px) reveals the next one, and the scroll after the last point moves on. Phones keep a third of a screen per beat (touch flicks travel
      // much further). Beats are spread evenly; the configured length is for a film.
      var count = Math.max(1, beats.length);
      root.style.setProperty('--pdp2-jar-len-desktop', 'calc(100vh - var(--pdp2-jar-top, 0px) + ' + (100 * (count - 1) + 40) + 'px)');
      root.style.setProperty('--pdp2-jar-len-mobile', 110 + 30 * count + 'vh');
      // Beat i switches at i x 100px (a little early, so one wheel step always
      // lands on it); the last scroll after the last beat carries the chapter on.
      var dist = 100 * (count - 1) + 40;
      beats.forEach(function (b, i) {
        b.at = Math.min(1, (i * 100 - 25) / dist);
      });
      if (beats[0]) beats[0].at = 0;
      cfg.beatsStart = 0.04;
      cfg.filmStart = 0.9;
      cfg.filmEnd = 0.95;
      function draw() {
        if (destroyed || halted) return;
        natW = poster.naturalWidth;
        natH = poster.naturalHeight;
        if (!natW || !natH) return stayPoster('poster failed to load');
        measure();
        if (halted) return;
        drawSource(poster);
        schedule();
      }
      if (poster.complete) {
        // Already settled: loaded draws, failed falls back to the static chapter.
        if (poster.naturalWidth) draw();
        else stayPoster('poster failed to load');
      } else {
        poster.addEventListener('load', draw, { once: true });
        poster.addEventListener('error', function () { stayPoster('poster failed to load'); }, { once: true });
      }
    }

    function prepare() {
      if (prepared || destroyed) return;
      prepared = true;
      if (!video || !video.dataset.src) return showStill();

      video.addEventListener(
        'loadedmetadata',
        function () {
          if (destroyed) return;
          natW = video.videoWidth;
          natH = video.videoHeight;
          if (!natW || !natH) return stayPoster('no video metadata');
          if (video.duration > cfg.maxDuration) return stayPoster('video longer than ' + cfg.maxDuration + 's');
          measure();
          harvest(
            function () {
              mode = 'frames';
              setState('frames');
              video.removeAttribute('src');
              video.load();
              schedule();
            },
            function (why) {
              fallbackToSeek(why);
            }
          );
        },
        { once: true }
      );
      video.addEventListener(
        'error',
        function () {
          stayPoster('video failed to load');
        },
        { once: true }
      );
      video.preload = 'auto';
      video.src = video.dataset.src;
      video.load();
    }

    /* ---- wiring --------------------------------------------------------- */

    function onScroll() {
      if (active) schedule();
    }
    function onResize() {
      relayout();
    }

    var io = null;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            active = e.isIntersecting;
            if (active) {
              prepare();
              schedule();
            }
          });
        },
        { rootMargin: '100% 0px 100% 0px' }
      );
      io.observe(root);
    } else {
      active = true;
      prepare();
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    if (DESKTOP.addEventListener) DESKTOP.addEventListener('change', onResize);
    function onMotionPref() {
      if (reduceMotion.matches) {
        if (video) video.pause();
        goStatic();
      }
    }
    if (reduceMotion.addEventListener) reduceMotion.addEventListener('change', onMotionPref);

    var ro = null;
    if ('ResizeObserver' in window && copy) {
      ro = new ResizeObserver(function () {
        relayout();
      });
      ro.observe(copy);
    }
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        if (!destroyed) relayout();
      });
    }

    // First paint on the canvas from the poster, in the right pose, so the
    // hand-over to the first video frame is invisible.
    function posterReady() {
      if (destroyed) return;
      relayout();
    }
    if (poster) {
      if (poster.complete && poster.naturalWidth) posterReady();
      else poster.addEventListener('load', posterReady, { once: true });
    }
    measure();
    schedule();

    teardowns[root.dataset.sectionId || root.id] = function () {
      destroyed = true;
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      if (DESKTOP.removeEventListener) DESKTOP.removeEventListener('change', onResize);
      if (reduceMotion.removeEventListener) reduceMotion.removeEventListener('change', onMotionPref);
      if (io) io.disconnect();
      if (ro) ro.disconnect();
      if (video) {
        video.pause();
        video.removeEventListener('seeked', onSeeked);
      }
      Object.keys(bitmaps).forEach(function (k) {
        bitmaps[k].close();
      });
      bitmaps = {};
      frames = [];
    };
  }

  function init(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-pdp2-jar]'), initJar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var id = e.detail && e.detail.sectionId;
    if (id && teardowns[id]) {
      teardowns[id]();
      delete teardowns[id];
    }
  });
})();
